import { useEffect } from "react";
import moment from "moment";
import styled from "styled-components";
import {
  faCheck,
  faCircleCheck,
  faCircleXmark,
  faEllipsis,
  faSpinner,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HoverCard, Title } from "@mantine/core";

import { Link } from "@components/Link";
import { useBasePath, useRepos, useScans } from "@hooks";
import { Scan } from "@types";
import { MINUTE } from "@utils";

import { ScanStatus } from "@pages/dashboard/types";
import { getScanStatus } from "@pages/dashboard/utils";
import { Repo } from "@pages/projects/types";

const RECENT_SCAN_WINDOW_MS = 60 * 60 * 1000;
const STARTING_FAST_REFRESH_WINDOW_MS = 5 * MINUTE;

const ScanStatusDropdown = styled(HoverCard.Dropdown)`
  width: 340px;
  color: var(--dark-gray1);
  padding: 0;
  max-height: 320px;
  overflow-y: auto;
  margin-top: 20px;
`;

const ScanStatusDropdownTitle = styled.div`
  padding: 8px;
`;

const OverlayEntryDiv = styled.div<{ $first?: boolean; $last?: boolean }>`
  display: flex;
  align-items: center;
  padding: ${(p) => (p.$first ? `16px` : `8px`)} 8px
    ${(p) => (p.$last ? `16px` : `8px`)} 8px;
  line-height: 1.3;
  color: var(--dark-gray1);
  :hover {
    background-color: lightgrey;
  }
`;

const OverlayEntryIconDiv = styled.div`
  padding: 0 16px 0 4px;
`;

interface RepoScan {
  scan: Scan | null;
  repo: Repo;
}

const scanStatusIcon = (status: ScanStatus) => {
  switch (status) {
    case "starting":
      return (
        <FontAwesomeIcon
          color="var(--global-dark-purple)"
          icon={faEllipsis}
          size="lg"
        />
      );
    case "running":
      return (
        <FontAwesomeIcon
          color="var(--global-dark-purple)"
          icon={faSpinner}
          pulse
          size="lg"
        />
      );
    case "succeeded":
      return (
        <FontAwesomeIcon
          color="var(--global-green)"
          icon={faCircleCheck}
          size="lg"
        />
      );
    case "never_started":
      return (
        <FontAwesomeIcon
          color="var(--global-red)"
          icon={faCircleXmark}
          size="lg"
        />
      );
    case "never_finished":
      return (
        <FontAwesomeIcon
          color="var(--global-red)"
          icon={faCircleXmark}
          size="lg"
        />
      );
    case "error":
      return (
        <FontAwesomeIcon
          color="var(--global-red)"
          icon={faCircleXmark}
          size="lg"
        />
      );
  }
};

const scanStatusText = (rs: RepoScan) => {
  const { scan, repo } = rs;
  const status = getScanStatus(repo, scan);

  switch (status) {
    case "starting":
      return "waiting for scan to start";
    case "running":
      return `started ${moment(scan!.started_at).fromNow()}`;
    case "succeeded":
      return `finished ${moment(scan!.completed_at).fromNow()}`;
    case "never_started":
      return `never started`;
    case "never_finished":
      return `never finished`;
    case "error":
      return `error (exit code ${scan!.exit_code})`;
  }
};

const OverlayEntry: React.FC<{
  repoScan: RepoScan;
  first?: boolean;
  last?: boolean;
}> = ({ repoScan: { repo, scan }, first, last }) => {
  const scanStatus = getScanStatus(repo, scan);
  const icon = scanStatusIcon(scanStatus);
  const text = scanStatusText({ scan: scan, repo: repo });
  return (
    <OverlayEntryDiv $first={first} $last={last}>
      <OverlayEntryIconDiv>{icon}</OverlayEntryIconDiv>
      <div>
        <div>
          <strong>{repo.name}</strong>
        </div>
        <div>{text}</div>
      </div>
    </OverlayEntryDiv>
  );
};

export const ScanStatusIndicator: React.FC = () => {
  const basePath = useBasePath();
  const { data: repos, refetch: refetchRepos } = useRepos();
  const reposMap = repos?.reduce<{ [index: number]: Repo }>(
    (o, repo) => ({ ...o, [repo.id!]: repo }),
    {}
  );

  const newRepoExists = repos?.reduce<boolean>(
    (o, repo) =>
      o ||
      moment(repo.provisioned_at).isAfter(
        moment().subtract(STARTING_FAST_REFRESH_WINDOW_MS, "ms")
      ),
    false
  );

  const params = new URLSearchParams({
    since: ((Date.now() - RECENT_SCAN_WINDOW_MS) / 1000).toString(),
  });
  const { data: recentScans } = useScans(newRepoExists, params);

  // recentScans and repos are not necessarily in sync,
  // so check that no scans reference each repo. These checks are
  // in order of increasing complexity
  const recentlySetUpRepos = repos?.filter(
    (repo) =>
      !repo.latest_scan_at &&
      moment(repo.provisioned_at).isAfter(
        moment().subtract(RECENT_SCAN_WINDOW_MS, "ms")
      ) &&
      recentScans &&
      !recentScans.find((scan) => scan.repository_id === repo.id)
  );

  useEffect(() => {
    refetchRepos();
  }, [recentScans, refetchRepos]);

  const scanRepos: RepoScan[] = (
    recentlySetUpRepos
      ? recentlySetUpRepos.map(
          (repo) => ({ scan: null, repo: repo } as RepoScan)
        )
      : []
  ).concat(
    recentScans && reposMap
      ? recentScans.map(
          (scan) =>
            ({
              scan: scan,
              repo: reposMap[scan.repository_id],
            } as RepoScan)
        )
      : []
  );

  // filter to only the first scan for each repo
  // and only those scans that have a repo for them (otherwise we wait until the repo is populated)
  const filteredScanRepos = scanRepos?.filter((sr) => {
    return (
      sr.repo && (sr.scan === null || sr.repo.first_scan_id === sr.scan.id!)
    );
  });

  const [inProgressScans, finishedScans] = filteredScanRepos?.reduce(
    (prev, current) => {
      const status = getScanStatus(current.repo, current.scan);
      if (status === "running" || status === "starting") {
        return [prev[0] + 1, prev[1]];
      } else {
        return [prev[0], prev[1] + 1];
      }
    },
    [0, 0]
  );

  const headerText =
    inProgressScans > 0
      ? `${inProgressScans} new scan${
          inProgressScans > 1 ? "s" : ""
        } in progress`
      : `${finishedScans} new scan${
          finishedScans > 1 ? "s" : ""
        } recently completed`;

  const openContents = (
    <>
      <ScanStatusDropdownTitle>
        <Title order={5}>{headerText}</Title>
      </ScanStatusDropdownTitle>
      <div>
        {filteredScanRepos.map((sr, i) => (
          <Link
            to={`${basePath}/findings?repo=${sr.repo.name!}`}
            key={`scan-overlay-entry-${i}`}
          >
            <OverlayEntry
              repoScan={sr}
              first={i === 0}
              last={i === filteredScanRepos.length - 1}
            />
          </Link>
        ))}
      </div>
    </>
  );

  const statusIcon =
    inProgressScans && inProgressScans > 0 ? (
      <FontAwesomeIcon icon={faSpinner} pulse />
    ) : finishedScans && finishedScans > 0 ? (
      <FontAwesomeIcon icon={faCheck} color="var(--global-green)" />
    ) : undefined;

  return (
    <HoverCard closeDelay={500} shadow="md" position="right" offset={40}>
      <HoverCard.Target>
        <div data-testid="scan-status-hover-target">{statusIcon}</div>
      </HoverCard.Target>
      <ScanStatusDropdown data-testid="scan-status-indicator">
        {openContents}
      </ScanStatusDropdown>
    </HoverCard>
  );
};
